const NOTIFICATION_LIMIT = 20;
import createOrderIcon from "../../../assets/icons/images/create_order_v2.png";
import { fromCentsToPounds } from "../../../helpers/money";
import { getProfilePublicPath } from "../../../helpers/profilePaths";
import { getUserDisplayName } from "../../../helpers/userDisplayName";

// Helper function to truncate long text for single-line display
const truncateLine = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength
        ? `${text.substring(0, maxLength)}...`
        : text;
};

const getNotifierName = (user) => {
    const username = String(user?.userName ?? user?.username ?? '').replace(/^@/, '').trim();
    if (username) return username;
    return getUserDisplayName(user, { fallback: "Someone" });
};

const notificationTypes = [
    // ===== SOCIAL INTERACTIONS =====
    // Likes on content
    {
        type: "like_creation",
        icon: "like_creation",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} liked your creation`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/creations/${notification?.creation?.creation_id}`,
    },
    {
        type: "like_news",
        icon: "like_creation",
        iconProps: {},
        isImage: false,
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} liked your news`,
        getDescription: (notification) => notification?.news?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) => `/news/${notification?.news_id}`,
    },

    // Comments on content
    {
        type: "comment_creation",
        icon: "comment",
        iconProps: {
            color: "none",
            stroke: "#FF8CDC"
        },
        isImage: false,
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} "${truncateLine(notification?.comment?.content)}"`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/creations/${notification?.creation?.creation_id}`,
    },
    {
        type: "comment_news",
        icon: "comment_creation",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} commented: "${truncateLine(notification?.comment_news?.content)}"`,
        getDescription: (notification) => notification?.news?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) => `/news/${notification?.news_id}`,
    },

    // Profile interactions
    {
        type: "new_subscriber",
        icon: "new_subscriber",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} subscribed to your profile`,
        getDescription: (notification) => {
            // Show Creation Title if it exists, else News Title if it exists, else empty
            if (notification?.creation?.title) {
                return notification.creation.title;
            } else if (notification?.news?.title) {
                return notification.news.title;
            }
            return '';
        },
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            getProfilePublicPath(notification?.notifier) ||
            `/profiles/${notification?.notifier?.user_id}`,
    },

    // ===== CONTENT CREATION =====
    // New content published
    {
        type: "new_news",
        icon: "new_news",
        isImage: false,
        iconProps: { stroke: "#FF8CDC", color: "#FF8CDC"},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} has news`,
        getDescription: (notification) => notification?.news?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) => `/news/${notification?.news_id}`,
    },
    {
        type: "new_creation",
        icon: "new_creation",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} published a new creation`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/creations/${notification?.creation?.creation_id}`,
    },

    // ===== E-COMMERCE & ORDERS =====
    // Order lifecycle notifications
    {
        type: "create_order",
        icon: createOrderIcon,  
        isImage: true,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} has made an order.`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/orders/${notification?.order_id}`,
    },
    {
        type: "received_shipment",
        icon: "received_shipment",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} has received your shipment`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/creations/${notification?.creation?.creation_id}`,
    },
    {
        type: "complete_shipment",
        icon: "delivery-truck",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} has shipped your order`,
        getDescription: (notification) => notification?.creation?.title,
        getEdition: (notification) => {
            if (notification?.creation?.media) {
                return notification.creation.media?.max_stock && notification.creation.media?.max_stock > 0 ? 'Limited Edition' : 'Unlimited Edition';
            }
            return null;
        },
        uri: (notification) =>
            `/creations/${notification?.creation?.creation_id}`,
    },

    // ===== CONTRIBUTIONS & SUPPORT =====
    // Financial support notifications - BACKEND NEEDS TO BE IMPLEMENTED
    {
        type: "contribution",
        icon: "one_time_contribution",
        isImage: false,
        iconProps: { },
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} contributed to you`,
        getDescription: (notification) => notification?.creation?.title || 'Support',
        getEdition: (notification) => {
            const amount = fromCentsToPounds(notification?.payments?.feeBreakdown?.baseAmountInCents || 0);
            return `${amount} • One-time`;
        },
        uri: (notification) => notification?.creation?.creation_id 
            ? `/creations/${notification?.creation?.creation_id}`
            : '/earnings',
    },
    {
        type: "monthly_contribution", 
        icon: "month_contribution",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${getNotifierName(notification?.notifier)} started monthly support`,
        getDescription: (notification) => notification?.creation?.title || 'General support',
        getEdition: (notification) => {
            const amount = fromCentsToPounds(notification?.payments?.feeBreakdown?.baseAmountInCents || 0);
            return `${amount}/month • Recurring`;
        },
        uri: (notification) => notification?.creation?.creation_id 
            ? `/creations/${notification?.creation?.creation_id}`
            : '/earnings',
    },
    {
        type: "invitation_done", 
        icon: "new_subscriber",
        isImage: false,
        iconProps: {},
        getTitle: (notification) => `${notification?.invitation?.invited_user?.userName}  just joined HYK `,
        getDescription: (notification) => 'through your invitation',
        
        uri: (notification) =>
            getProfilePublicPath(notification?.invitation?.invited_user) ||
            `/profiles/${notification?.invitation?.invited_user?.user_id}`,
    },
];

const notificationTypeMap = notificationTypes.reduce((acc, type) => {
    acc[type.type] = type;
    return acc;
}, {});

export { notificationTypes, notificationTypeMap, NOTIFICATION_LIMIT };